import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * DuplicateDepartmentsDialog
 *
 * Mirror of DuplicateCategoriesDialog. Calls /products/duplicate-departments
 * and /products/merge-departments. Same two-section layout:
 *   • Duplicate groups   → merge with Title-Case canonical (editable)
 *   • Orphans (rename)   → collapsible, each row has its own rename input
 */
export default function DuplicateDepartmentsDialog({ open, onClose, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [data, setData] = useState(null);
  const [groupCanonical, setGroupCanonical] = useState({});
  const [orphanRename, setOrphanRename] = useState({});
  const [orphansOpen, setOrphansOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/duplicate-departments`);
      setData(res.data);
      const gc = {};
      (res.data.duplicate_groups || []).forEach((g) => { gc[g.key] = g.default_canonical; });
      setGroupCanonical(gc);
      const or = {};
      (res.data.orphans || []).forEach((o) => {
        const current = o.variants[0]?.name || "";
        or[o.key] = (current === o.default_canonical) ? "" : o.default_canonical;
      });
      setOrphanRename(or);
    } catch (e) {
      toast.error("Failed to load department duplicates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchData();
    else { setData(null); setGroupCanonical({}); setOrphanRename({}); setOrphansOpen(false); }
  }, [open]);

  const mergeOne = async (variants, toName) => {
    const toClean = (toName || "").trim();
    if (!toClean) {
      toast.error("Canonical name cannot be empty");
      return false;
    }
    try {
      const res = await axios.post(`${API}/products/merge-departments`, {
        from: variants.map((v) => v.name),
        to: toClean,
      });
      toast.success(`${toClean}: ${res.data.products_updated} product(s) updated`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to merge into ${toClean}`);
      return false;
    }
  };

  const handleMergeGroup = async (g) => {
    setWorking(true);
    const ok = await mergeOne(g.variants, groupCanonical[g.key]);
    setWorking(false);
    if (ok) { await fetchData(); onComplete?.(); }
  };

  const handleMergeAllGroups = async () => {
    if (!data?.duplicate_groups?.length) return;
    if (!window.confirm(
      `This will merge ${data.duplicate_groups.length} duplicate group(s) using the canonical names shown. Continue?`
    )) return;
    setWorking(true);
    let okCount = 0;
    for (const g of data.duplicate_groups) {
      const ok = await mergeOne(g.variants, groupCanonical[g.key]);
      if (ok) okCount++;
    }
    setWorking(false);
    toast.success(`Merged ${okCount}/${data.duplicate_groups.length} group(s)`);
    await fetchData();
    onComplete?.();
  };

  const handleRenameOrphan = async (o) => {
    const newName = (orphanRename[o.key] || "").trim();
    if (!newName) return;
    setWorking(true);
    const ok = await mergeOne(o.variants, newName);
    setWorking(false);
    if (ok) { await fetchData(); onComplete?.(); }
  };

  const handleRenameAllOrphans = async () => {
    const queued = (data?.orphans || []).filter((o) => (orphanRename[o.key] || "").trim());
    if (!queued.length) {
      toast.info("No orphans queued for rename — fill in a new name first");
      return;
    }
    if (!window.confirm(`Rename ${queued.length} department(s)?`)) return;
    setWorking(true);
    let okCount = 0;
    for (const o of queued) {
      const ok = await mergeOne(o.variants, orphanRename[o.key]);
      if (ok) okCount++;
    }
    setWorking(false);
    toast.success(`Renamed ${okCount}/${queued.length}`);
    await fetchData();
    onComplete?.();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-departments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Clean Up Departments
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Scanning departments…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline">{data.summary.total_distinct_departments} distinct</Badge>
              <Badge variant={data.summary.duplicate_group_count > 0 ? "destructive" : "secondary"}>
                {data.summary.duplicate_group_count} duplicate group(s)
              </Badge>
              <Badge variant="outline">{data.summary.orphan_count} unique department{data.summary.orphan_count === 1 ? "" : "s"}</Badge>
            </div>

            {data.duplicate_groups.length === 0 && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                ✓ No case-insensitive duplicates found.
              </div>
            )}

            {data.duplicate_groups.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">Duplicate groups (merge candidates)</h3>
                  <Button size="sm" onClick={handleMergeAllGroups} disabled={working} data-testid="merge-all-dept-groups-btn">
                    {working ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Merge all using suggested names
                  </Button>
                </div>
                {data.duplicate_groups.map((g) => (
                  <div key={g.key} className="border rounded p-3 space-y-2 bg-amber-50/40" data-testid={`dup-dept-group-${g.key}`}>
                    <div className="text-xs text-gray-600">
                      {g.total_products} product(s) across {g.variants.length} variant(s):
                    </div>
                    <ul className="text-sm font-mono pl-3 space-y-0.5">
                      {g.variants.map((v) => (
                        <li key={v.name}>
                          <span className="text-gray-500">[{v.product_count}]</span>{" "}
                          <span className="text-gray-900">{v.name}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center gap-2 pt-2">
                      <span className="text-xs text-gray-600 shrink-0">Merge into →</span>
                      <Input
                        value={groupCanonical[g.key] || ""}
                        onChange={(e) => setGroupCanonical((prev) => ({ ...prev, [g.key]: e.target.value }))}
                        className="h-8 text-sm"
                        data-testid={`canonical-dept-input-${g.key}`}
                      />
                      <Button size="sm" onClick={() => handleMergeGroup(g)} disabled={working} data-testid={`merge-dept-group-${g.key}`}>
                        Merge
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {data.orphans.length > 0 && (
              <div className="border rounded">
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  onClick={() => setOrphansOpen((v) => !v)}
                  data-testid="toggle-dept-orphans-btn"
                >
                  <span className="flex items-center gap-1">
                    {orphansOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    Optional: rename other departments ({data.orphans.length})
                  </span>
                </button>
                {orphansOpen && (
                  <div className="border-t p-3 space-y-2">
                    <div className="flex justify-end">
                      <Button size="sm" variant="outline" onClick={handleRenameAllOrphans} disabled={working} data-testid="rename-all-dept-orphans-btn">
                        Rename all (where new name is filled)
                      </Button>
                    </div>
                    {data.orphans.map((o) => {
                      const current = o.variants[0]?.name || "";
                      return (
                        <div key={o.key} className="grid grid-cols-12 gap-2 items-center text-sm" data-testid={`dept-orphan-row-${o.key}`}>
                          <div className="col-span-5 font-mono">
                            <span className="text-gray-500">[{o.total_products}]</span> {current}
                          </div>
                          <div className="col-span-5">
                            <Input
                              value={orphanRename[o.key] ?? ""}
                              onChange={(e) => setOrphanRename((prev) => ({ ...prev, [o.key]: e.target.value }))}
                              placeholder={`(leave blank to skip · suggested: ${o.default_canonical})`}
                              className="h-8 text-sm"
                              data-testid={`dept-orphan-input-${o.key}`}
                            />
                          </div>
                          <div className="col-span-2">
                            <Button
                              size="sm" variant="outline" className="w-full"
                              disabled={working || !(orphanRename[o.key] || "").trim() || (orphanRename[o.key] || "").trim() === current}
                              onClick={() => handleRenameOrphan(o)}
                              data-testid={`rename-dept-orphan-${o.key}`}
                            >
                              Rename
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-dup-depts-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
